import {
  FaCheckCircle,
  FaClipboardCheck,
  FaRegClock,
  FaShieldAlt,
  FaTasks,
} from "react-icons/fa";
import Seo from "../../components/Seo";
import ServicePageTemplate from "../../components/ServicePageTemplate";
import { createBreadcrumbSchema } from "../../lib/seo";
import ServiceCTASection from "../../components/ServiceCTASection";

const seo = {
  title: "Prior Authorization Service For Healthcare Providers",
  description:
    "Physicians Success Partners (PSP) provides fast prior authorization services, reducing delays and denials while ensuring quick payer approvals.",
  keywords:
    "prior authorization service, authorization workflows, payer authorization support",
  schema: [
    {
      "@context": "https://schema.org",
      "@type": "Service",
      name: "Prior Authorization Services",
      url: "https://psprcm.us/services/prior-authorization/",
      description:
        "Physicians Success Partners (PSP) provides fast prior authorization services, reducing delays and denials while ensuring quick payer approvals.",
      serviceType: "Prior Authorization",
      provider: {
        "@type": "Organization",
        name: "Physicians Success Partners",
        url: "https://psprcm.us/",
        logo: "https://psprcm.us/logo/main-logo.webp",
        telephone: "+1 6155536787",
        email: "info@psprcm.us",
        address: {
          "@type": "PostalAddress",
          streetAddress: "1616 Westgate Circle",
          addressLocality: "Brentwood",
          addressRegion: "TN",
          postalCode: "37027",
          addressCountry: "USA",
        },
      },
      areaServed: {
        "@type": "Country",
        name: "United States",
      },
      audience: {
        "@type": "MedicalAudience",
        audienceType: "Healthcare Providers",
      },
      availableChannel: {
        "@type": "ServiceChannel",
        serviceLocation: {
          "@type": "Place",
          address: { "@type": "PostalAddress", addressCountry: "USA" },
        },
        availableLanguage: ["English"],
      },
      hasOfferCatalog: {
        "@type": "OfferCatalog",
        name: "Prior Authorization Solutions",
        itemListElement: [
          {
            "@type": "Offer",
            itemOffered: { "@type": "Service", name: "Insurance Verification" },
          },
          {
            "@type": "Offer",
            itemOffered: {
              "@type": "Service",
              name: "Pre-Certification Services",
            },
          },
          {
            "@type": "Offer",
            itemOffered: {
              "@type": "Service",
              name: "Medical Necessity Documentation",
            },
          },
          {
            "@type": "Offer",
            itemOffered: {
              "@type": "Service",
              name: "Authorization Submission & Follow-Up",
            },
          },
          {
            "@type": "Offer",
            itemOffered: {
              "@type": "Service",
              name: "Payer Communication & Status Tracking",
            },
          },
        ],
      },
    },
    createBreadcrumbSchema(
      "/services/prior-authorization",
      "Expert Prior Authorization Support by Physicians Success Partners",
    ),
  ],
};

export default function PriorAuthorization() {
  return (
    <>
      <Seo
        title={seo.title}
        metaTitle={seo.title}
        description={seo.description}
        keywords={seo.keywords}
        canonicalPath="/services/prior-authorization"
        schema={seo.schema}
      />
      <ServicePageTemplate
        banner={{
          heading: "Prior Authorization Service For Healthcare Providers",
          paragraph:
            "Physicians Success Partners (PSP) delivers complete prior authorization services, partnering closely with healthcare providers to eliminate approval delays, reduce authorization denials, and ensure every procedure, medication, and service your patients need is approved by payers quickly.",
          image: "/img/services-img/prior-authorization.webp",
        }}
        grid={{
          kicker: "Authorization Support",
          heading:
            "Prior Authorization Services That Get Your Patients Approved Faster",
          description: `At Physicians Success Partners (PSP), our prior authorization services are built around eliminating the administrative bottlenecks that delay patient care and disrupt your revenue cycle simultaneously. Our certified prior authorization specialist team brings deep expertise across all major commercial payers, Medicare Advantage plans, and Medicaid programs. 
          We manage every step of the insurance prior authorization process, from clinical documentation gathering and criteria review to submission, follow-up, and appeal filing, so your patients receive timely care approvals and your practice avoids costly claim denials caused by missing or delayed authorizations.`,
          metrics: [
            {
              icon: <FaTasks />,
              value: "20+",
              label: "Years of Combined Authorization Management Experience",
              style: "metricPrimary",
            },
            {
              icon: <FaRegClock />,
              value: "94%",
              label: "First Submission Approval Rate",
              style: "metricAccent",
            },
            {
              icon: <FaClipboardCheck />,
              value: "48 Hrs",
              label: "Average Authorization Turnaround Time",
              style: "metricLight",
            },
            {
              icon: <FaShieldAlt />,
              value: "100%",
              label: "HIPAA Compliance",
              style: "metricDark",
            },
          ],
          panelEyebrow: "Request Visibility",
          panelHeading:
            "Optimized for Consistent and Accurate Prior Authorization Outcomes at Every Stage",
          assurances: [
            {
              icon: <FaCheckCircle />,
              label: "Accurate Clinical Documentation & Criteria Review",
            },
            {
              icon: <FaTasks />,
              label: "Reliable Payer Submission & Status Follow-Up ",
            },
            {
              icon: <FaRegClock />,
              label: "Timely Approval Tracking & Appeal Management",
            },
          ],
        }}
        services={{
          heading:
            "PSP's Top Rated Prior Authorization Services Group Is Here for Your Practice",
          description: `PSP's prior authorization specialist team manages your entire authorization lifecycle from initial clinical review to final payer approval confirmation. We identify documentation gaps, prevent authorization denials, and implement best practices that ensure compliant and complete prior auth submissions at every stage. 
          Our structured pre-authorization services approach helps practices of all sizes eliminate care delays, protect revenue, and reduce the administrative burden that unmanaged authorization workflows place on your internal staff every single day.`,
          items: [
            {
              title: "Accurate Prior Auth Submission",
              text: "Our prior authorization services team reviews every clinical requirement carefully gathering medical records, physician notes, and payer-specific criteria so every authorization request goes out complete, well-documented, and ready for fast payer approval without unnecessary back-and-forth delays.",
            },
            {
              title: "Steadier Patient Care & Revenue Flow",
              text: "Our prior authorization specialist team protects both patient access to care and your practice revenue by ensuring authorizations are obtained proactively, tracked systematically, and renewed before expiration,  preventing last-minute claim denials and treatment interruptions.",
            },
            {
              title: "Reduced Administrative Burden",
              text: "By managing complex authorization management externally, we free your internal clinical and administrative staff from navigating payer portals, gathering documentation, and making follow-up calls, so your team stays focused entirely on delivering quality patient care.",
            },
            {
              title: "Compliance-Minded Authorization Execution",
              text: "We stay fully aligned with payer medical necessity guidelines, clinical criteria standards, and documentation requirements so your pre-authorization services process remains dependable, organized, audit-ready, and compliant at every stage of the approval workflow.",
            },
          ],
        }}
        faq={{
          image: "/img/services-img/prior-authorization.webp",
          imageAlt: "Prior authorization support",
          title: "Why Choose PSP for Prior Authorization?",
          description:
            " Our prior authorization teams are more than just administrative processors. We are every USA provider's most trusted prior authorization services partner. Our insurance prior authorization advocates work alongside physicians, group practices, and healthcare organizations to eliminate approval delays, manage complex payer criteria, and ensure every patient receives the care they need without unnecessary administrative obstacles. ",
        }}
        specialtiesContent={{
          eyebrow: "Authorization Support",
          heading:
            "Specialty support tailored to the needs of modern practices.",
          description:
            "Explore the specialties we support through dependable billing workflows, organized documentation support, and professional revenue cycle execution.",
        }}
      />
      <ServiceCTASection
        imageSrc="/img/services-img/cta-section-img.webp"
        title="Dedicated Prior Authorization Specialist for Your Clinic's Care Approval Workflow"
        description="Unmanaged prior authorization services gaps can delay critical patient care, trigger avoidable claim denials, and cost your practice thousands of dollars in lost reimbursements every single month. PSP's certified prior authorization specialist team streamlines your entire authorization management process from medical necessity review and clinical documentation gathering to payer submission, real-time status tracking, denial appeals, and expiration renewal management all supported by live authorization dashboards and detailed reporting so you maintain complete visibility and control over every pending and active authorization request at all times."
        btnText="Let's Connect"
      />
    </>
  );
}
